import { useCallback } from 'react';
import type { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import ConceptContext from './ConceptContext';
import ConceptsScreen from './ConceptsScreen.Container';
import ConceptDetailsScreen from './ConceptDetailsScreen';
import EffectScreen from './EffectScreen';
import ResultScreen from './ResultScreen';
import CombinationScreen from './CombinationScreen.Container';
import DialogTransitionRoute from '../../utils/DialogTransitionRoute';
import { nextRound } from '../../store/PlaythroughSlice';
import { useService } from '@xstate/react';
import interpreter from '../../state/service';
import type { Concept } from '../../data/Concepts';
import { hasNewActivatedCombinations } from '../../data/Combinations';

export interface StateProps {
	activeConcepts: Concept[];
}

export interface DispatchProps {}

export interface OwnProps {}

type Props = StateProps & DispatchProps & OwnProps;

const Concepts: FunctionComponent<Props> = (props: Props) => {
	const { activeConcepts } = props;
	const dispatch = useDispatch();
	const [state, send] = useService(interpreter);

	const openConceptDetails = useCallback(
		(concept: Concept) =>
			send('CONCEPT_DETAILS', { conceptName: concept.name }),
		[send]
	);
	const activateConcept = useCallback(
		(concept: Concept) =>
			send('CONCEPT_ACTIVATE', { conceptName: concept.name }),
		[send]
	);
	const next = useCallback(() => send('NEXT'), [send]);
	const closeDialog = useCallback(() => send('CLOSE'), [send]);
	const finishConceptActivation = useCallback(
		(concept: Concept) => {
			dispatch(nextRound({ concept }));
			send('CLOSE');
		},
		[dispatch, send]
	);
	const showCombinationOrFinishConceptActivation = useCallback(
		(concept: Concept) => {
			if (hasNewActivatedCombinations(concept, activeConcepts)) {
				send('NEXT');
			} else {
				finishConceptActivation(concept);
			}
		},
		[send, activeConcepts, finishConceptActivation]
	);

	return (
		<>
			<DialogTransitionRoute
				key="concepts_overview"
				state={state}
				stateId="concepts.overview"
			>
				<ConceptsScreen
					onClose={closeDialog}
					onConcept={openConceptDetails}
					onActivate={activateConcept}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute
				key="concept_details"
				state={state}
				stateId="concepts.details"
			>
				<ConceptContext
					conceptName={state.context.conceptName}
					render={(concept) => (
						<ConceptDetailsScreen
							concept={concept}
							onBack={closeDialog}
							onActivate={activateConcept}
						/>
					)}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute
				key="effect"
				state={state}
				stateId="concepts.effect"
			>
				<ConceptContext
					conceptName={state.context.conceptName}
					render={(concept) => <EffectScreen concept={concept} onNext={next} />}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute
				key="result"
				state={state}
				stateId="concepts.result"
			>
				<ConceptContext
					conceptName={state.context.conceptName}
					render={(concept) => (
						<ResultScreen
							concept={concept}
							onNext={showCombinationOrFinishConceptActivation}
						/>
					)}
				/>
			</DialogTransitionRoute>
			<DialogTransitionRoute
				key="combination"
				state={state}
				stateId="concepts.combination"
			>
				<ConceptContext
					conceptName={state.context.conceptName}
					render={(concept) => (
						<CombinationScreen
							concept={concept}
							onNext={finishConceptActivation}
						/>
					)}
				/>
			</DialogTransitionRoute>
		</>
	);
};

export default Concepts;
